import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { FiDownload, FiStar } from 'react-icons/fi';

import FormatCurrency from '../../utils/FormatCurrency';
import FormatPrice, { isFreePrice } from '../../utils/FormatPrice';
import FavoriteButton from '../FavoriteButton';

import {
  Author,
  Badge,
  Categories,
  Category,
  Container,
  Description,
  FavoriteSlot,
  Foot,
  Headline,
  Image,
  InfoContainer,
  NameContainer,
  PreviousPrice,
  Price,
  Rate,
  RatingContainer,
  Stat,
  Thumb,
} from './styles';

const propTypes = {
  project: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    designerUser: PropTypes.shape({ name: PropTypes.string.isRequired })
      .isRequired,
    shortDescription: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    previousPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    premium: PropTypes.bool,
    downloadsCount: PropTypes.number,
    images: PropTypes.arrayOf(PropTypes.object),
    categories: PropTypes.arrayOf(PropTypes.object),
    averageRating: PropTypes.string,
  }).isRequired,
};

const ProjectCard = ({
  project: {
    id,
    name,
    designerUser,
    categories,
    shortDescription,
    price,
    images,
    previousPrice,
    premium,
    downloadsCount,
    averageRating,
  },
}) => {
  const history = useHistory();
  const free = isFreePrice(price);

  // A mesma categoria pode vir repetida da API (a tabela de junção não tem
  // restrição de unicidade), e ids repetidos quebram as keys da lista.
  const uniqueCategories = categories
    ? categories.filter(
        (c, index) => categories.findIndex(other => other.id === c.id) === index
      )
    : [];

  function onCardClick() {
    history.push(`/projects/${id}`);
  }

  function handleKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onCardClick();
    }
  }

  return (
    <Container
      role="link"
      tabIndex={0}
      aria-label={`${name} — ver projeto`}
      onClick={onCardClick}
      onKeyDown={handleKeyDown}
    >
      <Thumb>
        {images && images.length > 0 && (
          <Image src={images[0].url} alt="" loading="lazy" />
        )}
        {(free || premium) && (
          <Badge free={free ? 1 : 0}>{free ? 'Grátis' : 'Pro'}</Badge>
        )}
        <FavoriteSlot>
          <FavoriteButton projectId={id} />
        </FavoriteSlot>
      </Thumb>

      <InfoContainer>
        <NameContainer>
          <Headline>{name}</Headline>
          {parseFloat(averageRating) > 0 && (
            <RatingContainer>
              <Rate>{averageRating}</Rate>
              <FiStar size={14} color="var(--laranja)" fill="var(--laranja)" />
            </RatingContainer>
          )}
        </NameContainer>

        <Author>{designerUser.name}</Author>

        {uniqueCategories.length > 0 && (
          <Categories>
            {uniqueCategories.slice(0, 2).map(c => (
              <Category key={c.id}>{c.name}</Category>
            ))}
          </Categories>
        )}

        {shortDescription && <Description>{shortDescription}</Description>}

        <Foot>
          <div>
            {!free && previousPrice && (
              <PreviousPrice>{FormatCurrency(previousPrice)}</PreviousPrice>
            )}
            <Price free={free ? 1 : 0}>{FormatPrice(price)}</Price>
          </div>
          {downloadsCount > 0 && (
            <Stat>
              <FiDownload size={13} />
              {downloadsCount.toLocaleString('pt-BR')}
            </Stat>
          )}
        </Foot>
      </InfoContainer>
    </Container>
  );
};

ProjectCard.propTypes = propTypes;

export default ProjectCard;
